class CustomShopifyA11y {
  static getFocusableElements(container) {
    if (window.FoxTheme?.a11y?.getFocusableElements) {
      return FoxTheme.a11y.getFocusableElements(container);
    }

    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }

  static decodeBase64List(value) {
    if (!value) return [];

    try {
      return atob(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  static setCurrent(element, active, value = 'location') {
    if (!element) return;

    element.classList.toggle('active', active);
    if (active) {
      element.setAttribute('aria-current', value);
    } else {
      element.removeAttribute('aria-current');
    }
  }

  static getScrollBehavior() {
    return window.FoxTheme?.config?.motionReduced ? 'auto' : 'smooth';
  }
}

class CustomCopyText extends HTMLElement {
  constructor() {
    super();
    this.textArea = null;
    this.initCopyEvent();
  }
  initCopyEvent() {
    this.addEventListener('click', async (event) => {
      const text = this.dataset.copyValue;
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.handleSuccess();
      } catch (err) {
        this.fallbackCopy(text);
      }
    });
  }
  handleSuccess() {
    this.dispatchEvent(new CustomEvent('copy-success'));
  }
  fallbackCopy(text) {
    if (!this.textArea) {
      this.textArea = document.createElement('textarea');
      this.textArea.style.position = 'fixed';
      this.textArea.style.left = '-9999px';
      document.body.appendChild(this.textArea);
    }
    this.textArea.value = text;
    this.textArea.select();
    try {
      document.execCommand('copy');
      this.handleSuccess();
    } catch (err) {
      console.error('fail:', err);
    } finally {
      document.body.removeChild(this.textArea);
      this.textArea = null;
    }
  }
}
customElements.define('custom-copy-text', CustomCopyText);

class CustomCouponCode extends HTMLElement {
  constructor() {
    super();
    this.copyText = this.querySelector('custom-copy-text');
    this.copyText.addEventListener('copy-success', () => {
      this.copyText.classList.add('ctm-cct_copied');
      setTimeout(() => {
        this.copyText.classList.remove('ctm-cct_copied');
      }, 5000);
    });
  }
}
customElements.define('custom-coupon-code', CustomCouponCode);

class CustomSellPoint extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.items = [];
    this.panels = [];
    this.desktopSummaries = [];
    this.mainMedia = [];
    this.overlayMedia = [];
    this.dots = [];
    this.prevButton = null;
    this.nextButton = null;
    this.slideshow = null;
    this.mobileCards = [];
    this.mobileProgress = null;
    this.mobileCarousel = null;
    this.mobileSlider = null;
    this.activeIndex = -1;
    this.defaultIndex = 0;
    this.hoverResetTimer = null;
    this.summaryHideTimers = new Map();
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.ctm-c_trg[role="tab"]'));
    this.items = Array.from(this.querySelectorAll('.ctm-c_item'));
    this.panels = Array.from(this.querySelectorAll('.ctm-c_pnl[role="tabpanel"]'));
    this.desktopSummaries = Array.from(this.querySelectorAll('[data-summary-index]'));
    this.mainMedia = Array.from(this.querySelectorAll('[data-media-type="main"]'));
    this.overlayMedia = Array.from(this.querySelectorAll('[data-media-type="overlay"]'));
    this.singleMedia = Array.from(this.querySelectorAll('[data-media-type="single"]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.mobileCarousel = this.querySelector('[data-mobile-carousel]');
    this.slideshow = this.querySelector('[data-mobile-track]');
    this.mobileCards = Array.from(this.querySelectorAll('[data-mobile-card]'));
    this.mobileProgress = this.querySelector('[data-mobile-progress]');
    this.mediaQuery = window.matchMedia('(max-width: 74.9rem)');

    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        if (this.canHoverDesktop() && event.detail > 0) return;
        this.activate(index, { moveFocus: false, userInitiated: true });
      });
      tab.addEventListener('focus', () => this.activate(index, { moveFocus: false, scrollIntoView: false }));
      tab.addEventListener('mouseenter', () => {
        if (this.canHoverDesktop()) {
          this.clearHoverReset();
          this.activate(index, { moveFocus: false, scrollIntoView: false });
        }
      });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.activate(index, { moveFocus: false, userInitiated: true }));
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrevious({ userInitiated: true }));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNext({ userInitiated: true }));
    }

    this.addEventListener('mouseleave', () => {
      if (this.canHoverDesktop()) {
        this.scheduleHoverReset();
      }
    });
    this.addEventListener('mouseenter', () => this.clearHoverReset());

    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.mediaQuery.addEventListener('change', this.handleViewportChange);

    this.defaultIndex = this.getInitialIndex();
    this.activate(this.defaultIndex, { moveFocus: false, scrollIntoView: false });
    this.initMobileCarousel();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleViewportChange);
    }
    this.destroyMobileCarousel();
    this.clearHoverReset();
    this.summaryHideTimers.forEach((timer) => window.clearTimeout(timer));
    this.summaryHideTimers.clear();
  }

  onKeydown(event, index) {
    let nextIndex = index;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (index + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activate(index, { moveFocus: false, userInitiated: true });
        return;
      default:
        return;
    }

    event.preventDefault();
    this.activate(nextIndex, { moveFocus: true, userInitiated: true });
  }

  activate(index, { moveFocus = false, scrollIntoView = true, userInitiated = false } = {}) {
    if (index < 0 || index >= this.tabs.length) return;

    const shouldAnimate = this.activeIndex !== -1 && this.activeIndex !== index;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${panelId}`) : this.panels[tabIndex];

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', '0');
      tab.classList.toggle('is-active', isActive);

      if (this.items[tabIndex]) {
        this.items[tabIndex].classList.toggle('is-active', isActive);
      }

      if (panel) {
        panel.toggleAttribute('hidden', !isActive);
        panel.classList.toggle('is-active', isActive);
        if (isActive && shouldAnimate) {
          this.animateElement(panel, 'is-panel-entering');
        }
      }
    });

    this.toggleSummaries(index, shouldAnimate);
    this.toggleMedia(this.mainMedia, index, shouldAnimate);
    this.toggleMedia(this.overlayMedia, index, shouldAnimate);
    this.toggleMedia(this.singleMedia, index, shouldAnimate);
    this.toggleDots(index);
    this.toggleMobileCards(index);
    this.activeIndex = index;

    if (scrollIntoView) {
      if (this.isMobileViewport()) {
        this.syncMobileCarousel(index, { instant: !userInitiated });
      } else if (this.tabs[index]) {
        this.tabs[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    if (moveFocus && this.tabs[index]) {
      this.tabs[index].focus();
    }

    this.updateMobileProgress();
  }

  goToNext(options = {}) {
    const nextIndex = (this.activeIndex + 1) % this.tabs.length;
    this.activate(nextIndex, options);
  }

  goToPrevious(options = {}) {
    const previousIndex = (this.activeIndex - 1 + this.tabs.length) % this.tabs.length;
    this.activate(previousIndex, options);
  }

  toggleMedia(mediaItems, activeIndex) {
    mediaItems.forEach((mediaItem, index) => {
      const isActive = index === activeIndex;
      mediaItem.removeAttribute('hidden');
      mediaItem.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      mediaItem.classList.toggle('is-active', isActive);
    });
  }

  toggleSummaries(activeIndex, shouldAnimate = false) {
    this.desktopSummaries.forEach((summary, index) => {
      const isActive = index === activeIndex;
      const hideTimer = this.summaryHideTimers.get(summary);

      if (hideTimer) {
        window.clearTimeout(hideTimer);
        this.summaryHideTimers.delete(summary);
      }

      if (isActive) {
        summary.removeAttribute('hidden');

        if (shouldAnimate) {
          void summary.offsetWidth;
        }

        summary.classList.add('is-active');
        return;
      }

      summary.classList.remove('is-active');

      if (!shouldAnimate || summary.hasAttribute('hidden')) {
        summary.setAttribute('hidden', '');
        return;
      }

      const timer = window.setTimeout(() => {
        if (!summary.classList.contains('is-active')) {
          summary.setAttribute('hidden', '');
        }
        this.summaryHideTimers.delete(summary);
      }, 760);

      this.summaryHideTimers.set(summary, timer);
    });
  }

  toggleDots(activeIndex) {
    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  toggleMobileCards(activeIndex) {
    this.mobileCards.forEach((card, index) => {
      card.classList.toggle('is-active', index === activeIndex);
    });
  }

  initMobileCarousel() {
    if (
      !this.mobileCarousel ||
      !this.slideshow ||
      !this.mobileCards.length ||
      !this.isMobileViewport() ||
      !window.FoxTheme ||
      !window.FoxTheme.Carousel
    ) {
      return;
    }

    if (this.mobileSlider) return;

    const sliderArgs = {
      slidesPerView: 'auto',
      spaceBetween: 10,
      loop: false,
      threshold: 5,
      watchSlidesProgress: true,
      resistanceRatio: 0.72,
      navigation: false,
      pagination: false,
      on: {
        init: (swiper) => this.updateMobileProgress(swiper),
        progress: (swiper) => this.updateMobileProgress(swiper),
        setTranslate: (swiper) => this.updateMobileProgress(swiper),
        slideChange: (swiper) => this.setMobileActive(swiper.realIndex ?? swiper.activeIndex ?? 0),
        transitionEnd: (swiper) => this.setMobileActive(swiper.realIndex ?? swiper.activeIndex ?? 0),
        touchEnd: (swiper) => this.setMobileActive(swiper.realIndex ?? swiper.activeIndex ?? 0)
      }
    };

    this.mobileSlider = new window.FoxTheme.Carousel(this.mobileCarousel, sliderArgs, [FoxTheme.Swiper.Mousewheel]);
    this.mobileSlider.init();

    this.syncMobileCarousel(this.activeIndex > -1 ? this.activeIndex : 0, { instant: true });
  }

  destroyMobileCarousel() {
    if (!this.mobileSlider) return;

    this.mobileSlider.slider.destroy(true, true);
    this.mobileSlider = null;
  }

  getInitialIndex() {
    const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return selectedIndex > -1 ? selectedIndex : 0;
  }

  updateMobileProgress(swiperInstance = null) {
    if (!this.mobileProgress || !this.mobileCards.length) return;

    const slider = swiperInstance || (this.mobileSlider && this.mobileSlider.slider ? this.mobileSlider.slider : null);

    if (slider) {
      const sliderProgress = slider.progress;
      if (typeof sliderProgress === 'number') {
        const startProgress = 1 / this.mobileCards.length;
        const progress = startProgress + (1 - startProgress) * Math.max(0, Math.min(sliderProgress, 1));
        this.mobileProgress.style.transform = `scaleX(${Math.max(0, Math.min(progress, 1))})`;
        return;
      }
    }

    const total = this.mobileCards.length;
    const currentIndex = slider ? slider.activeIndex : this.activeIndex;
    const safeIndex = currentIndex > -1 ? currentIndex : 0;
    const progress = total <= 1 ? 1 : (safeIndex + 1) / total;
    this.mobileProgress.style.transform = `scaleX(${Math.max(0, Math.min(progress, 1))})`;
  }

  setMobileActive(index) {
    if (typeof index !== 'number' || index < 0 || index >= this.mobileCards.length) return;

    this.activeIndex = index;
    this.toggleMobileCards(index);
    this.updateMobileProgress();
  }

  syncMobileCarousel(index, { instant = false } = {}) {
    if (!this.mobileSlider || !this.mobileCards.length || index < 0 || index >= this.mobileCards.length) {
      return;
    }

    if (this.mobileSlider.slider.activeIndex !== index) {
      this.mobileSlider.slider.slideTo(index, instant ? 0 : undefined);
    }

    this.updateMobileProgress();
  }

  isMobileViewport() {
    return this.mediaQuery ? this.mediaQuery.matches : window.matchMedia('(max-width: 74.9rem)').matches;
  }

  handleViewportChange() {
    if (this.isMobileViewport()) {
      this.initMobileCarousel();
      this.syncMobileCarousel(this.activeIndex > -1 ? this.activeIndex : this.getInitialIndex(), { instant: true });
    } else {
      this.destroyMobileCarousel();
    }
    this.updateMobileProgress();
  }

  animateElement(element, className) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
  }

  handleBlockSelect(event) {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    const matchingTab = this.tabs.find((tab) => tab.id.indexOf(blockId) > -1);
    if (!matchingTab) return;

    const index = this.tabs.indexOf(matchingTab);
    if (index < 0) return;

    this.activate(index, { moveFocus: !this.isMobileViewport(), userInitiated: true });
  }

  canHoverDesktop() {
    return window.matchMedia('(min-width: 75rem) and (hover: hover) and (pointer: fine)').matches;
  }

  scheduleHoverReset() {
    this.clearHoverReset();
    this.hoverResetTimer = window.setTimeout(() => {
      this.activate(this.defaultIndex, { moveFocus: false, scrollIntoView: false });
    }, 180);
  }

  clearHoverReset() {
    if (!this.hoverResetTimer) return;

    window.clearTimeout(this.hoverResetTimer);
    this.hoverResetTimer = null;
  }
}

if (!customElements.get('custom-sell-point')) {
  customElements.define('custom-sell-point', CustomSellPoint);
}
class CustomAccordionMenu extends HTMLElement {
  constructor() {
    super();
    this.handleTitleClick = this.handleTitleClick.bind(this);
  }

  connectedCallback() {
    this.initAccordion();
    this.bindEvents();
  }

  initAccordion() {
    this.accordionTitles = this.querySelectorAll('.accordion-menu__title');
    this.accordions = this.querySelectorAll('.accordion-menu');
  }

  bindEvents() {
    if (this.accordionTitles.length) {
      this.accordionTitles.forEach(title => {
        title.addEventListener('click', this.handleTitleClick);
      });
    }
  }

  handleTitleClick(e) {
    const currentAccordion = e.currentTarget.closest('.accordion-menu');

    this.accordions.forEach(accordion => {
      if (accordion !== currentAccordion) {
        accordion.classList.remove('active');
      }
    });

    currentAccordion.classList.toggle('active');
  }

  setDefaultActive() {
    if (this.accordions.length) {
      this.accordions[0].classList.add('active');
    }
  }

  disconnectedCallback() {
    if (this.accordionTitles.length) {
      this.accordionTitles.forEach(title => {
        title.removeEventListener('click', this.handleTitleClick);
      });
    }
  }
}
customElements.define('custom-accordion-menu', CustomAccordionMenu);


class customCompareTable extends HTMLElement {
  constructor() {
    super();
    this.navLinks = [];
    this.sections = [];
    this.observer = null;
    this.pendingActiveId = null;
    this.pendingActiveTimer = null;
    this.stickyTop = 0;
    this.stageFrame = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    document.documentElement.classList.add('has-ctm-compar');
    document.body.classList.add('has-ctm-compar');
    this.navLinks = Array.from(this.querySelectorAll('[data-compar-nav]'));
    this.sections = Array.from(this.querySelectorAll('[data-compar-section]'));
    this.intro = this.querySelector('.ctm-compar_intro');
    this.introInner = this.querySelector('.ctm-compar_intro-inner');
    this.body = this.querySelector('.ctm-compar_body');
    this.header = this.querySelector('.ctm-compar_head');
    this.rowsViewport = this.querySelector('.ctm-compar_rows-viewport');
    this.rows = this.querySelector('.ctm-compar_rows');

    if (!this.navLinks.length || !this.sections.length) return;

    this.navLinks.forEach((link) => {
      link.addEventListener('click', (event) => this.onNavClick(event, link));
    });

    this.initObserver();
    this.updateTableStage();
    window.setTimeout(() => this.updateTableStage(), 250);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('load', this.handleResize);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    window.requestAnimationFrame(() => {
      if (!document.querySelector('comparison-anchor')) {
        document.documentElement.classList.remove('has-ctm-compar');
        document.body.classList.remove('has-ctm-compar');
      }
    });
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.pendingActiveTimer) {
      window.clearTimeout(this.pendingActiveTimer);
    }
    if (this.stageFrame) {
      window.cancelAnimationFrame(this.stageFrame);
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('load', this.handleResize);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  onNavClick(event, link) {
    const targetId = link.getAttribute('href')?.slice(1);
    const target = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;

    if (!target) return;

    event.preventDefault();
    this.lockActive(target.id, !this.prefersReducedMotion.matches);
    this.scrollToSection(target, !this.prefersReducedMotion.matches, true);
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.isDesktopTableScroll()) return;
        if (this.pendingActiveId) return;

        if (entries.some((entry) => entry.isIntersecting)) {
          this.setActive(this.getCurrentSectionId());
        }
      },
      {
        rootMargin: '-25% 0px -55% 0px',
        threshold: [0.1, 0.35, 0.6]
      }
    );

    this.sections.forEach((section) => this.observer.observe(section));
  }

  setActive(sectionId) {
    this.navLinks.forEach((link) => {
      const isActive = link.getAttribute('href') === `#${sectionId}`;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  lockActive(sectionId, shouldWait = true) {
    this.pendingActiveId = sectionId;
    this.setActive(sectionId);

    if (this.pendingActiveTimer) {
      window.clearTimeout(this.pendingActiveTimer);
    }

    this.pendingActiveTimer = window.setTimeout(() => {
      this.pendingActiveId = null;
      this.pendingActiveTimer = null;
      this.setActive(sectionId);
    }, shouldWait ? 1200 : 0);
  }

  getCurrentSectionId() {
    const header = this.querySelector('.ctm-compar_head');
    const headerStyle = header ? window.getComputedStyle(header) : null;
    const headerSpace = header ? header.getBoundingClientRect().height + parseFloat(headerStyle?.marginBottom || 0) : 0;
    const markerTop = (this.stickyTop || this.getStickyTop()) + headerSpace + 1;
    let currentSection = this.sections[0];

    this.sections.forEach((section) => {
      if (section.getBoundingClientRect().top <= markerTop) {
        currentSection = section;
      }
    });

    return currentSection?.id;
  }

  scrollToSection(section, shouldAnimate = true, shouldUseTableScroll = false) {
    if (shouldUseTableScroll && this.isDesktopTableScroll()) {
      this.scrollRowsToSection(section, shouldAnimate);
      return;
    }

    const targetTop = this.getSectionPageTarget(section);

    window.scrollTo({
      top: targetTop,
      behavior: shouldAnimate ? 'smooth' : 'auto'
    });
  }

  handleScroll() {
    if (this.stageFrame) return;

    this.stageFrame = window.requestAnimationFrame(() => {
      this.stageFrame = null;
      this.applyTableScroll();
    });
  }

  handleResize() {
    this.updateTableStage();
  }

  isDesktopTableScroll() {
    return window.matchMedia('(min-width: 990px)').matches && this.body && this.header && this.rows && this.rowsViewport;
  }

  isComponentInWheelRange() {
    const rect = this.getBoundingClientRect();
    const stickyTop = this.stickyTop || this.getStickyTop();

    return rect.bottom > stickyTop + 1 && rect.top < window.innerHeight - 1;
  }

  updateTableStage({ preserveRowShift = false } = {}) {
    if (!this.isDesktopTableScroll()) {
      this.style.removeProperty('--ctm-compar-table-h');
      this.style.removeProperty('--ctm-compar-left-h');
      this.style.removeProperty('--ctm-compar-stage-h');
      this.style.removeProperty('--ctm-compar-view-h');
      this.style.removeProperty('--ctm-compar-sticky-top');
      this.style.removeProperty('--ctm-compar-head-left');
      this.style.removeProperty('--ctm-compar-head-width');
      this.style.removeProperty('--ctm-compar-head-space');
      this.style.removeProperty('--ctm-compar-mask-left');
      this.style.removeProperty('--ctm-compar-mask-width');
      this.style.removeProperty('--ctm-compar-mask-height');
      this.style.removeProperty('--ctm-compar-rows-view-h');
      this.classList.remove('is-table-sticky');
      return;
    }

    const stickyTop = this.getStickyTop();
    this.stickyTop = stickyTop;
    const introHeight = this.introInner ? this.introInner.getBoundingClientRect().height : 0;
    const headerStyle = window.getComputedStyle(this.header);
    const headerBlock = this.header.getBoundingClientRect().height + parseFloat(headerStyle.marginBottom || 0);
    const rowsHeight = this.rows.scrollHeight;
    const tableHeight = headerBlock + rowsHeight;
    const viewHeight = Math.max(260, window.innerHeight - stickyTop);

    this.style.setProperty('--ctm-compar-table-h', `${tableHeight}px`);
    this.style.setProperty('--ctm-compar-left-h', `${Math.max(introHeight, tableHeight)}px`);
    this.style.setProperty('--ctm-compar-stage-h', `${Math.max(introHeight, tableHeight)}px`);
    this.style.setProperty('--ctm-compar-view-h', `${viewHeight}px`);
    this.style.setProperty('--ctm-compar-sticky-top', `${stickyTop}px`);
    this.style.setProperty('--ctm-compar-head-space', `${headerBlock}px`);
    this.style.setProperty('--ctm-compar-rows-view-h', `${Math.max(0, viewHeight - headerBlock)}px`);
    this.updateHeaderMetrics();
  }

  applyTableScroll() {
    if (!this.isDesktopTableScroll()) return;

    this.updateStickyOffset();
    this.updateHeaderMetrics();
    if (!this.pendingActiveId) {
      this.setActive(this.getCurrentSectionId());
    }
  }

  scrollRowsToSection(section, shouldAnimate = true) {
    this.updateTableStage({ preserveRowShift: true });

    window.scrollTo({
      top: this.getSectionPageTarget(section),
      behavior: shouldAnimate && !this.prefersReducedMotion.matches ? 'smooth' : 'auto'
    });
  }

  getSectionPageTarget(section) {
    const stickyTop = this.stickyTop || this.getStickyTop();
    const headerStyle = this.header ? window.getComputedStyle(this.header) : null;
    const headerSpace = this.header ? this.header.getBoundingClientRect().height + parseFloat(headerStyle?.marginBottom || 0) : 0;

    return Math.max(0, section.getBoundingClientRect().top + window.scrollY - stickyTop - headerSpace);
  }

  updateHeaderMetrics() {
    if (!this.isDesktopTableScroll()) return;

    const stickyTop = this.stickyTop || this.getStickyTop();
    const headerStyle = window.getComputedStyle(this.header);
    const headerSpace = this.header.getBoundingClientRect().height + parseFloat(headerStyle.marginBottom || 0);
    const bodyRect = this.body.getBoundingClientRect();
    const componentRect = this.getBoundingClientRect();
    const shouldMask = componentRect.top <= stickyTop && componentRect.bottom > stickyTop + headerSpace;

    this.style.setProperty('--ctm-compar-head-space', `${headerSpace}px`);
    this.style.setProperty('--ctm-compar-mask-left', `${bodyRect.left}px`);
    this.style.setProperty('--ctm-compar-mask-width', `${bodyRect.width}px`);
    this.style.setProperty('--ctm-compar-mask-height', `${stickyTop + headerSpace}px`);
    this.style.setProperty('--ctm-compar-rows-view-h', `${Math.max(0, window.innerHeight - stickyTop - headerSpace)}px`);
    this.classList.toggle('is-table-sticky', shouldMask);
  }

  updateStickyOffset() {
    const nextStickyTop = this.getStickyTop();

    if (Math.abs(nextStickyTop - this.stickyTop) < 1) return;

    this.stickyTop = nextStickyTop;
    this.style.setProperty('--ctm-compar-sticky-top', `${nextStickyTop}px`);
  }

  getStickyTop() {
    let stickyTop = this.getCssPixelValue('--header-height');
    const headerCandidates = document.querySelectorAll('.ctm-header-wrapper--sticky, .sticky-header, sticky-header, header[sticky], header.site-header');

    headerCandidates.forEach((header) => {
      if (this.contains(header)) return;

      const style = window.getComputedStyle(header);
      const rect = header.getBoundingClientRect();
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0;
      const isPinned = (style.position === 'sticky' || style.position === 'fixed') && rect.top <= 1 && rect.bottom > 1;

      if (isVisible) {
        stickyTop = Math.max(stickyTop, rect.height);
      }

      if (isPinned) {
        stickyTop = Math.max(stickyTop, rect.bottom);
      }
    });

    return stickyTop;
  }

  getCssPixelValue(name) {
    const probe = document.createElement('div');

    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.height = `var(${name}, 0px)`;
    document.body.appendChild(probe);

    const value = probe.getBoundingClientRect().height;
    probe.remove();

    return value;
  }

  handleBlockSelect(event) {
    const section = event.target?.closest?.('[data-compar-section]');
    if (!section || !this.contains(section)) return;

    this.setActive(section.id);
    this.scrollToSection(section, false, true);
  }
}

customElements.define('comparison-anchor', customCompareTable);

class CustomAnchor extends HTMLElement {
  constructor() {
    super();
    this.anchorIds = {};
    this.lists = [];
    this.observers = [];
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  set currentAnchor(value) {
    const old = this._currentAnchor;
    if (old === value) return;
    CustomShopifyA11y.setCurrent(old, false);
    CustomShopifyA11y.setCurrent(value, true);
    this._currentAnchor = value;
  }

  connectedCallback() {
    this.lists = Array.from(this.querySelectorAll(this.anchorSelector));

    for (const list of this.lists) {
      for (const anchorId of CustomShopifyA11y.decodeBase64List(list.dataset?.anchorId)) {
        const anchor = document.getElementById(anchorId);
        if (!anchor) continue;
        this.anchorIds[anchorId] = list;
        this.initObserverAnchor(anchor);
      }
    }

    for (const list of this.lists) {
      list.addEventListener('click', this.handleBlockSelect);
    }
  }

  disconnectedCallback() {
    for (const list of this.lists) {
      list.removeEventListener('click', this.handleBlockSelect);
    }

    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }

  initObserverAnchor(targetAnchor) {
    const observerAnchor = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const anchor = this.anchorIds[id];
          this.currentAnchor = anchor;
        }
      });
    }, { rootMargin: `-15% 0px -70% 0px` });
    observerAnchor.observe(targetAnchor);
    this.observers.push(observerAnchor);
  }

  handleBlockSelect(event) {
    const firstId = CustomShopifyA11y.decodeBase64List(event.currentTarget?.dataset?.anchorId)[0];
    if (!firstId) return;

    const anchor = document.getElementById(firstId);
    if (!anchor) return;

    anchor.style.scrollMarginTop = `${this.topHeight}px`;
    anchor.scrollIntoView({ behavior: CustomShopifyA11y.getScrollBehavior(), inline: 'center', block: 'start' });
  }
}

class CustomSideAnchor extends CustomAnchor {
  constructor() {
    super();
    this.anchorSelector = '.ctm-sidean_top .ctm-sidean_list button';
    this.topHeight = 0;
    this.footerGap = 24;
    this.footerOffset = 0;
    this.rafId = null;
    this.handleViewportChange = this.handleViewportChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    window.addEventListener('resize', this.handleViewportChange);
    window.addEventListener('load', this.handleViewportChange, { once: true });
    this.handleViewportChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('load', this.handleViewportChange);

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  handleViewportChange() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.updateFooterOffset();
    });
  }

  updateFooterOffset() {
    const footer = document.querySelector('footer.footer, footer');

    if (!footer) {
      this.setFooterOffset(0);
      return;
    }

    const anchorRect = this.getBoundingClientRect();
    const footerRect = footer.getBoundingClientRect();
    const anchorBottom = anchorRect.bottom - this.footerOffset;
    const overlap = anchorBottom + this.footerGap - footerRect.top;
    this.setFooterOffset(overlap > 0 ? -overlap : 0);
  }

  setFooterOffset(value) {
    const nextOffset = Math.round(value);

    if (nextOffset === this.footerOffset) return;
    this.footerOffset = nextOffset;
    this.style.setProperty('--ctm-side-anchor-footer-offset', `${nextOffset}px`);
  }
}
customElements.define('custom-side-anchor', CustomSideAnchor);

class CustomTopAnchor extends CustomAnchor {
  constructor() {
    super();
    this.anchorSelector = '.ctm-topan button';
  }
  get topHeight() {
    return FoxTheme.config.mqlMobile ? 110 : 226;
  }
}
customElements.define('custom-top-anchor', CustomTopAnchor);
